/**
 * Inbox — the home screen. Mobile-first list of saved URLs. Top bar: paste
 * URL + state filter (inbox / archived / assigned). Each row taps to open
 * the article in a new tab; long-press / chevron opens the source detail
 * (quotes + assign). Pull-to-refresh via standard browser behaviour.
 */
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Archive, ChevronRight, ExternalLink, LogOut, Plus, RefreshCw, Search } from 'lucide-react';
import { api, redirectToLogin, type InboxRow } from '../lib/api';
import { BottomNav } from '../components/BottomNav';

type Filter = 'inbox' | 'archived' | 'assigned' | 'all';

export function Inbox() {
  const [rows, setRows] = useState<InboxRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [filter, setFilter] = useState<Filter>('inbox');
  const [q, setQ] = useState('');
  const [showAdd, setShowAdd] = useState(false);

  async function refresh() {
    setLoading(true);
    setErr(null);
    try {
      const params = new URLSearchParams({ state: filter, limit: '100' });
      if (q.trim()) params.set('q', q.trim());
      const r = await api.get<InboxRow[]>(`/research/inbox?${params}`);
      setRows(r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void refresh(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  async function logout() {
    // Server-side cookie clear — sends Set-Cookie with Max-Age=0 at the
    // .nightshade.gameonsolutions.uk domain so every subdomain loses session.
    try { await api.post('/auth/logout'); } catch { /* swallow */ }
    redirectToLogin();
  }

  return (
    <div className="min-h-screen flex flex-col">
      {/* Top bar */}
      <header className="sticky top-0 z-10 bg-bg/95 backdrop-blur border-b border-border px-4 py-3 flex items-center gap-2">
        <h1 className="font-serif text-xl flex-1">Research</h1>
        <button onClick={refresh} className="tap p-2" aria-label="Refresh">
          <RefreshCw className={`h-5 w-5 ${loading ? 'animate-spin' : ''}`} />
        </button>
        <button onClick={logout} className="tap p-2" aria-label="Sign out">
          <LogOut className="h-5 w-5 text-muted" />
        </button>
      </header>

      {/* Filter pills + search */}
      <div className="px-4 py-3 border-b border-border space-y-3">
        <div className="flex gap-2 overflow-x-auto -mx-4 px-4 pb-1">
          {(['inbox', 'archived', 'assigned', 'all'] as Filter[]).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`tap px-4 rounded-full text-sm whitespace-nowrap border transition ${
                filter === f
                  ? 'bg-accent border-accent text-bg'
                  : 'bg-card border-border text-text'
              }`}
            >
              {f}
            </button>
          ))}
        </div>
        <form onSubmit={(e) => { e.preventDefault(); void refresh(); }} className="flex gap-2">
          <div className="flex-1 relative">
            <Search className="absolute top-1/2 -translate-y-1/2 left-3 h-4 w-4 text-muted pointer-events-none" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search title, url, description"
              className="input pl-9"
              type="search"
            />
          </div>
        </form>
      </div>

      {/* List — pad bottom so last items aren't hidden under the bottom nav */}
      <main className="flex-1 px-4 py-3 space-y-3 pb-32">
        {err && (
          <div className="card border-danger/50 text-danger text-sm">
            {err}
          </div>
        )}
        {!loading && rows.length === 0 && (
          <p className="text-muted text-sm text-center py-12">
            {filter === 'inbox' ? 'Nothing in your inbox. Paste a URL below.' :
             filter === 'archived' ? 'No archived items.' :
             filter === 'assigned' ? 'No assigned items yet.' :
             'No items.'}
          </p>
        )}
        {rows.map((r) => <InboxCard key={r.id} row={r} onChange={refresh} />)}
      </main>

      {/* FAB — paste URL. Sit above the bottom nav (16px clearance). */}
      <button
        onClick={() => setShowAdd(true)}
        className="fixed bottom-24 right-6 tap rounded-full bg-accent text-bg shadow-lg p-4 hover:bg-accentSoft"
        aria-label="Add URL"
      >
        <Plus className="h-6 w-6" />
      </button>

      <BottomNav active="inbox" />

      {showAdd && <AddDialog onClose={() => setShowAdd(false)} onAdded={() => { setShowAdd(false); void refresh(); }} />}
    </div>
  );
}

function InboxCard({ row, onChange }: { row: InboxRow; onChange: () => void }) {
  async function archive(e: React.MouseEvent) {
    e.preventDefault();
    e.stopPropagation();
    try {
      await api.patch(`/research/inbox/${row.id}`, { state: row.state === 'archived' ? 'inbox' : 'archived' });
      onChange();
    } catch { /* swallow */ }
  }
  return (
    <Link to={`/source/${row.id}`} className="card flex gap-3 active:bg-border/30">
      {row.ogImage ? (
        <img
          src={row.ogImage}
          alt=""
          className="h-16 w-16 rounded-lg object-cover bg-border shrink-0"
          loading="lazy"
          onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }}
        />
      ) : (
        <div className="h-16 w-16 rounded-lg bg-border shrink-0 flex items-center justify-center text-muted text-xs">
          {row.ogSitename?.slice(0, 4) ?? '·'}
        </div>
      )}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-sm leading-tight line-clamp-2 break-words">
          {row.title ?? row.url}
        </h3>
        <p className="text-xs text-muted truncate mt-0.5">
          {row.ogSitename ?? hostnameOf(row.url)}
        </p>
        {row.ogDescription && (
          <p className="text-xs text-muted line-clamp-2 mt-1">{row.ogDescription}</p>
        )}
        <div className="flex items-center gap-3 mt-2 text-xs text-muted">
          {row.state === 'assigned' && <span className="text-accent">assigned</span>}
          {row.state === 'archived' && <span>archived</span>}
          {row.sourceKind === 'share-target' && <span>shared</span>}
          <span>{relTime(row.createdAt)}</span>
        </div>
      </div>
      <div className="flex flex-col gap-1 shrink-0">
        <a
          href={row.url}
          target="_blank"
          rel="noopener noreferrer"
          className="tap p-2 -mr-2"
          onClick={(e) => e.stopPropagation()}
          aria-label="Open original"
        >
          <ExternalLink className="h-4 w-4 text-muted" />
        </a>
        <button
          onClick={archive}
          className="tap p-2 -mr-2"
          aria-label={row.state === 'archived' ? 'Unarchive' : 'Archive'}
        >
          <Archive className="h-4 w-4 text-muted" />
        </button>
        <ChevronRight className="h-4 w-4 text-muted self-center" />
      </div>
    </Link>
  );
}

function AddDialog({ onClose, onAdded }: { onClose: () => void; onAdded: (r: InboxRow) => void }) {
  const [url, setUrl] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setErr(null);
    if (!url.trim()) return;
    setBusy(true);
    try {
      const r = await api.post<InboxRow>('/research/inbox', { url: url.trim() });
      onAdded(r);
    } catch (e2) {
      setErr(e2 instanceof Error ? e2.message : String(e2));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 flex items-end justify-center"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md card rounded-t-2xl rounded-b-none mb-0 space-y-3 m-0"
        style={{ paddingBottom: 'calc(1rem + env(safe-area-inset-bottom))' }}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 className="font-serif text-lg">Add URL</h2>
        <form onSubmit={submit} className="space-y-3">
          <input
            type="url"
            inputMode="url"
            autoFocus
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="https://…"
            className="input py-3"
          />
          {err && <p className="text-sm text-danger break-all">{err}</p>}
          <div className="flex gap-2">
            <button type="button" onClick={onClose} className="btn-ghost flex-1 py-3">Cancel</button>
            <button type="submit" disabled={busy} className="btn-primary flex-1 py-3">
              {busy ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function hostnameOf(url: string): string {
  try { return new URL(url).hostname.replace(/^www\./, ''); }
  catch { return url; }
}

function relTime(unix: number): string {
  const ms = Date.now() - unix * 1000;
  const s = Math.floor(ms / 1000);
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  if (d < 30) return `${d}d`;
  return new Date(unix * 1000).toLocaleDateString();
}
